<template>
  <section class="ui fluid search selection dropdown" :class="{ active: !hidden && focused }">
    <i class="dropdown icon" @click="focused ? inputBlured() : inputFocused()" />

    <div>
      <div
        class="ui label"
        :style="[colorLabelStyle(item)]"
        v-for="item in currentItemsUnique"
        :key="item.id"
      >
        <a href="#" @click.prevent="removeItem(item)"><i class="delete icon"></i></a>
        <slot name="selected" :item="item">
          {{ item.name | capitalize }}
        </slot>
      </div>

      <div class="ui input">
        <input
          class=" search"
          type="text"
          autocomplete="off"
          :placeholder="placeholderText | capitalize"
          :id="uid"
          v-model.trim="model"
          @focus="inputFocused()"
          @blur="inputBlured()"
          @keyup.up.exact="activateItem('up')"
          @keyup.down.exact="activateItem('down')"
          @keydown.tab.exact="selectItem(activeItem)"
          @keydown.esc.exact="hideResults()"
          @keyup.exact="inputKeyup($event)"
          @keydown.enter.prevent.exact="selectItem(activeItem)"
          @keydown.delete.exact="removeLastItem()"
          ref="inputField"
        />
      </div>
    </div>

    <nav
      id="selectionMenu"
      class="menu transition"
      :class="{ visible: focused && !hidden }"
      ref="selectionMenu"
    >
      <article
        class="item"
        :class="{ disabled: item.isChosen, selected: activeItem && activeItem.id === item.id }"
        @mousedown.prevent="selectItem(item)"
        v-for="item in displayedItems"
        :key="item.id"
      >
        <slot name="dropdown" :item="item">
          <color-label :color="item.color" size="tiny" v-if="useColors" />
          {{ item.name | capitalize }}
        </slot>
      </article>
      <article class="item disabled" v-if="!displayedItems.length">
        <p>{{ emptyText | capitalize }}</p>
      </article>
    </nav>
  </section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
.ui.dropdown {
  .ui.label {
    margin-right: 4px;
    margin-left: 0px;
    margin-top: 2px;
    margin-bottom: 2px;
  }
}

.ui.dropdown .menu > .item .dropdown.icon {
  margin-top: 1em;
}

.ui.input > input {
  padding: 0.4em 0.4em;
  float: left;
  width: 100%;
  border: none;
  background-color: transparent;
}

.ui.simple.dropdown .menu.transition.visible {
  border-top-width: 1px !important;
}

#selectionMenu {
  overflow-y: scroll;
  border-radius: 0.28571429rem !important;
  border-width: 1px !important;
}
</style>

<script>
// @flow
/**
 * Filter component.
 */

import Vue from 'vue'
import fuzzy from 'fuzzy'
import uuid from 'uuid'
import _ from 'lodash'
import ColorLabel from '../ColorLabel/ColorLabel'
import { Color } from 'services'
type Item = {
  name: string,
  id: string
}
export default {
  name: 'filterBy',
  components: { ColorLabel },
  props: {
    placeholderText: null,
    emptyText: null,
    items: {
      type: Array, // Items to select from.
      validator: (val: any) => val.reduce((prev, cur) => prev && cur.name && cur.id, true)
    },
    maxItems: {
      type: Number,
      default: () => 10
    },
    selectHandler: {
      type: Function,
      default: () => () => {}
    },
    removeHandler: {
      type: Function,
      default: () => () => {}
    },
    currentItems: {
      type: null, // Optional, only for 'multiple'.
      default: () => []
    },
    useColors: {
      type: Boolean,
      default: () => false
    }
  },

  computed: {
    nonSelectedItems() {
      const currentItemIds = this.currentItems.map(i => i.id)
      return _.map(this.items, i => {
        return { ...i, isChosen: _.includes(currentItemIds, i.id) }
      })
    },
    displayedItems() {
      const fuzzyFilterOptions = {
        extract: el => el.name
      }
      return fuzzy
        .filter(this.model, this.nonSelectedItems, fuzzyFilterOptions)
        .slice(0, this.maxItems)
        .map(item => item.original)
    },
    currentItemsUnique() {
      return _.uniqBy(this.currentItems, 'id')
    }
  },

  data() {
    return {
      uid: `filter${uuid.v4()}`,
      activePosition: 0,
      activeItem: null,
      focused: false,
      hidden: false,
      model: ''
    }
  },

  methods: {
    colorLabelStyle(c: any) {
      if (this.useColors) {
        return Color.colorLabelStyle(c)
      }
    },
    inputBlured() {
      this.focused = false
      this.hidden = true
      this.clearActiveItem()
    },

    inputFocused() {
      this.focused = true
      this.hidden = false
    },

    inputKeyup(e: any) {
      if (e.which !== this.keyCodes.enter && e.which !== this.keyCodes.esc) {
        this.hidden = false
      }

      if (!this.preventModelChange(e)) {
        this.activePosition = 0
        this.activeItem = this.displayedItems[0] || null
      }
    },

    preventModelChange(e: any) {
      return [
        this.keyCodes.enter,
        this.keyCodes.esc,
        this.keyCodes.arrowUp,
        this.keyCodes.arrowDown
      ].some(w => e.which === w)
    },

    clearActiveItem() {
      this.activeItem = this.displayedItems[0]
      this.activePosition = 0
      let selectionMenu = this.$refs.selectionMenu
      selectionMenu.scrollTop = 0
    },

    activateItem(modifier: string) {
      if (this.displayedItems.length) {
        let activePosition
        if (modifier === 'up') {
          activePosition = this.displayedItems[this.activePosition - 1]
            ? this.activePosition - 1
            : this.displayedItems.length - 1
        } else if (modifier === 'down') {
          activePosition = this.displayedItems[this.activePosition + 1]
            ? this.activePosition + 1
            : 0
        }
        this.scrollSelectionMenu(modifier, activePosition)
        this.activeItem = this.displayedItems[activePosition]
        this.activePosition = activePosition
      }
    },

    scrollSelectionMenu(modifier: string, activePosition: number) {
      let selectionMenu = this.$refs.selectionMenu
      const maxPossibleScroll = selectionMenu.scrollHeight - selectionMenu.clientHeight
      const scrollStep = maxPossibleScroll / (this.displayedItems.length - 1)
      if (modifier === 'up') {
        this.scrollUp(selectionMenu, activePosition, maxPossibleScroll, scrollStep)
      } else if (modifier === 'down') {
        this.scrollDown(selectionMenu, activePosition, maxPossibleScroll, scrollStep)
      }
    },

    scrollUp(
      selectionMenu: Object,
      activePosition: number,
      maxPossibleScroll: number,
      scrollStep: number
    ) {
      activePosition === 0
        ? (selectionMenu.scrollTop = 0)
        : activePosition === this.displayedItems.length - 1
        ? (selectionMenu.scrollTop = maxPossibleScroll)
        : (selectionMenu.scrollTop -= scrollStep)
    },

    scrollDown(
      selectionMenu: Object,
      activePosition: number,
      maxPossibleScroll: number,
      scrollStep: number
    ) {
      activePosition === this.displayedItems.length - 1
        ? (selectionMenu.scrollTop = maxPossibleScroll)
        : activePosition === 0
        ? (selectionMenu.scrollTop = 0)
        : (selectionMenu.scrollTop += scrollStep)
    },

    hideResults() {
      this.hidden = true
      this.clearActiveItem()
    },

    selectItem(item: any) {
      const selectedPosition = this.activePosition
      if (!item.isChosen) {
        this.hidden = true
        this.focused = false

        if (item) {
          this.handleSelectItem(item)
        }

        this.$refs.inputField.blur()
        this.$refs.inputField.focus()
        this.activeItem = this.displayedItems[selectedPosition]
        this.activePosition = selectedPosition
      }
    },

    handleSelectItem(item: any) {
      this.selectHandler(item)
      this.model = ''
    },

    removeItem(item: Item) {
      this.removeHandler(item)
      this.clearActiveItem()
      this.$refs.inputField.focus()
      this.$refs.inputField.blur()
    },

    removeLastItem() {
      if (this.model === '' && this.currentItems && this.currentItems.length > 0) {
        let lastItem = this.currentItems[this.currentItems.length - 1]
        this.removeItem(lastItem)
        this.$refs.inputField.focus()
      }
    }
  },

  watch: {
    items: {
      handler() {
        this.clearActiveItem()
      },
      deep: true
    }
  }
}
</script>
